import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer } from
'recharts';
import { LineChart, ZapIcon } from 'lucide-react';
import { useTelemetryHistory } from '../hooks/useHistoricalTelemetry';
import { whToKwh } from '../utils/energyUtils';
import { useTranslation } from 'react-i18next';
export function EnergyChart({ currentAssetId }: { currentAssetId: string | null }) {
  const { t } = useTranslation();
  const { data: telemetryData, loading, error } = useTelemetryHistory(
    currentAssetId || '',
    ['hourly_power'],
    'daily',
    'ASSET'
  );

  // Format data for Recharts
  const chartData = React.useMemo(() => {
    if (!telemetryData || !telemetryData['hourly_power']) {
      return [];
    }
    //console.log('telemetryData',telemetryData['hourly_power'])
    return [...telemetryData['hourly_power']]
      .sort((a, b) => a.ts - b.ts)
      .map((item: { ts: number; value: string }) => {
        const date = new Date(item.ts);
        const hours = date.getHours().toString().padStart(2, '0');
        //const minutes = date.getMinutes().toString().padStart(2, '0');
        return {
          time: `${hours}`,
          kwh: whToKwh(item.value)
        };
      });
  }, [telemetryData]);
  //console.log('chartData',chartData)
  const currentTotal = chartData.reduce((sum: number, item: any) => sum + item.kwh, 0);

  return (
    <div className="glass-panel rounded-2xl p-3 flex flex-col flex-1 h-full">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center space-x-2 text-slate-400">
          <ZapIcon className="w-4 h-4 text-amber-400" />
          <span className="text-[1rem] text-white font-medium uppercase tracking-wider">
            {t('energy_chart.title')}
          </span>
        </div>
        <span className="text-lg font-roboto font-bold text-white glow-text-amber">
          {currentTotal.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} <span className="text-xs text-slate-500 font-normal">kWh</span>
        </span>
      </div>

      <div className="w-full flex-1 min-h-0 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#0a0f1a]/50">
            <span className="text-sm text-amber-400 animate-pulse">{t('energy_chart.loading')}</span>
          </div>
        )}
        {!loading && chartData.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <span className="text-sm text-slate-500">{t('energy_chart.no_data')}</span>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorKwh" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{
                fill: '#64748b',
                fontSize: '0.7rem',
                fontFamily: 'JetBrains Mono'
              }}
              tickFormatter={(value) => `${value}h`}
              interval={1} />

            <YAxis hide />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0a0f1a',
                borderColor: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                fontFamily: 'JetBrains Mono',
                fontSize: '0.7rem'
              }}
              itemStyle={{
                color: '#f59e0b'
              }}
              formatter={(value: number) => [`${Number(value).toFixed(1)} kWh`, t('energy_chart.usage')]} />

            <Area
              type="monotone"
              dataKey="kwh"
              stroke="#f59e0b"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorKwh)" />

          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>);

}